// ────────────────────────────────────────────────────────────────────────────
// Whether an order may be billed, per the organisation's workflow policy.
//
// Kept apart from `mockWorkflow.ts` because the callers are different: that
// file serves the settings SCREEN and speaks in structural `Workflow` objects,
// while this answers a question the invoicing screens ask about a single order
// using the raw policy from the API.
//
// MIRRORS `WorkflowPolicyRules.IsInvoiceable` in the backend, which enforces
// the same rule on generate. The two must not drift — the failure mode is a
// list offering an order the server then refuses.
// ────────────────────────────────────────────────────────────────────────────
import type { OrderStatusValue, WorkflowPolicyConfig } from '@my/tms-api';

/** Policy key for an order status — the workflow's state keys are camelCase. */
export function orderStateKey(status: OrderStatusValue): string {
  switch (status) {
    case 'Draft':
      return 'draft';
    case 'Open':
      return 'open';
    case 'InProgress':
      return 'inProgress';
    case 'Completed':
      return 'completed';
    case 'Cancelled':
      return 'cancelled';
  }
}

/**
 * Whether an order in this state may appear in invoicing.
 *
 * <p>
 * `paymentMethod` is the Directory enum name — note the spelling bridge:
 * Directory says `SelfBill` where the policy document says `selfBilled`.
 * </p>
 *
 * <p>
 * An unknown or missing method falls through to the state switch rather than
 * refusing. Refusing to invoice because a party record is incomplete turns a
 * data-quality problem into an unpayable job.
 * </p>
 */
export function isOrderInvoiceable(
  policy: WorkflowPolicyConfig,
  paymentMethod: string | null | undefined,
  status: OrderStatusValue,
): boolean {
  // Never billed by us, whatever the state: the customer raises their own
  // document, or the work is inside the company.
  if (is(paymentMethod, 'SelfBill', 'selfBilled') || is(paymentMethod, 'Internal')) return false;

  // Cash is not configurable. The prepay rule (`customerPaid` on
  // open → inProgress) already requires the invoice to be settled while the
  // order is Open, so it has to be raisable from Open — a switch saying
  // otherwise would contradict a Block on the workflow screen.
  if (is(paymentMethod, 'Cash')) return status !== 'Draft' && status !== 'Cancelled';

  // Credit, and anything unrecognised: whatever the state's own switch says.
  return policy.states?.[orderStateKey(status)]?.invoiceable === true;
}

function is(value: string | null | undefined, ...names: string[]): boolean {
  return value != null && names.some((n) => n.toLowerCase() === value.toLowerCase());
}
